/** Presentation helpers — the vanilla app's money()/fmt()/dayFmt(), typed. */

/**
 * The locale every helper below formats in, kept as module state rather than
 * threaded through each call site.
 *
 * <LangProvider> writes it during render, so a helper called anywhere in the
 * tree — including from plain functions that cannot hold a hook — is already
 * on the right language by the time it runs. The default matches the app's
 * default language rather than the browser's, so a helper called before the
 * provider mounts still produces Indian formatting.
 */
let locale = 'en-IN';

export const setFormatLocale = (next: string) => {
  locale = next;
};

export const money = (n: number | null | undefined): string =>
  '₹' +
  Number(n || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Money for the public pages: whole rupees without the paise (₹3,000 rather
 * than ₹3,000.00), and paise only when there are some. Receipts, reports and
 * the dashboards keep [money]'s exact two decimals.
 */
export const moneyShort = (n: number | null | undefined): string => {
  const v = Number(n || 0);
  const whole = Math.abs(v - Math.round(v)) < 0.005;
  return '₹' + v.toLocaleString(locale, { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 });
};

// Times are always shown in 12-hour form. hour12 is forced rather than left to
// the browser's locale, which would render 24-hour clocks for e.g. en-GB users.
export const fmt = (ts: number): string =>
  new Date(ts).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short', hour12: true });

export const fmtT = (ts: number): string =>
  new Date(ts).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true });

export const dayFmt = (ts: number): string =>
  new Date(ts).toLocaleDateString(locale, { dateStyle: 'medium' });

/**
 * A booking's window. The end shows as a bare time when it lands on the same
 * day, but carries its date when the slot crosses midnight — otherwise a
 * two-day booking reads as "Aug 8, 10:00 AM → 8:00 AM", which looks like a
 * typo rather than a 46-hour slot.
 */
export const slotLabel = (start: number, end: number): string => {
  const sameDay = new Date(start).toDateString() === new Date(end).toDateString();
  return `${fmt(start)} → ${sameDay ? fmtT(end) : fmt(end)}`;
};

/**
 * An hour-of-day (0–23) as a 12-hour label: 6 -> "6 AM", 13 -> "1 PM".
 *
 * Built through Intl rather than by hand so the meridiem follows the language;
 * 24 is accepted and wraps, because a session that closes at midnight is
 * stored as `to: 24`.
 */
export const hourLabel = (hour: number): string => {
  const d = new Date(2000, 0, 1, hour % 24);
  return d.toLocaleTimeString(locale, { hour: 'numeric', hour12: true });
};

/** Month and year for a calendar heading, in the current language. */
export const monthLabel = (date: Date): string =>
  date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

/** "Saturday, 8 August" — the heading over a chosen day's bookings. */
export const longDayLabel = (date: Date): string =>
  date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

/**
 * The short weekday initials for a Monday-first grid.
 *
 * Intl lists them Sunday-first, so the first is moved to the end rather than
 * the letters being hard-coded — which is also what makes them Malayalam when
 * the site is.
 */
export const weekdayInitials = (): string[] => {
  const fmtDay = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  // 2024-01-07 was a Sunday; the week is read from there and rotated.
  const week = Array.from({ length: 7 }, (_, i) => fmtDay.format(new Date(2024, 0, 7 + i)));
  return [...week.slice(1), week[0]];
};

/** The twelve month names, for the jump-to-month panel. */
export const monthNames = (): string[] => {
  const fmtMonth = new Intl.DateTimeFormat(locale, { month: 'long' });
  return Array.from({ length: 12 }, (_, i) => fmtMonth.format(new Date(2024, i, 1)));
};

/** Today as YYYY-MM-DD, for <input type="date"> min/default. */
export const todayISO = (): string => {
  // Local, not toISOString(), which shifts to UTC and can hand back yesterday
  // for anyone east of Greenwich before their day is over.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Calendar days a booking holds when it runs a day or more, else 0. A
 * several-days hire is priced per day, so it reads as "3 days", not "62h".
 */
export const spanDays = (start: number, end: number): number => {
  if (end - start < 24 * 3_600_000) return 0;
  const a = new Date(start);
  const b = new Date(end - 1);
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;
};
